import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { SlidersHorizontal, X, Search, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "Mathematics",
  "English Language",
  "Integrated Science",
  "Social Studies",
  "Computing",
  "RME",
  "Creative Arts",
  "French",
  "Ghanaian Language"
];

const CLASS_LEVELS = ["Basic 7", "Basic 8", "Basic 9"];

export default function MockExamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { config: siteConfig } = useSiteCustomization();
  const classroomImg = siteConfig?.images?.classroom || "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/kt5ebc5j_WhatsApp%20Image%202026-02-09%20at%209.41.16%20PM.jpeg";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({});
  const [dynamicClassLevels, setDynamicClassLevels] = useState([]);
  const [dynamicSubjects, setDynamicSubjects] = useState([]);

  // Filter states
  const [selectedSubjects, setSelectedSubjects] = useState(
    searchParams.get("subject")?.split(",").filter(Boolean) || []
  );
  const [classLevel, setClassLevel] = useState(searchParams.get("class_level") || "");

  const [priceRange, setPriceRange] = useState([0, 150]);
  const [isBundle, setIsBundle] = useState(searchParams.get("is_bundle") === "true");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "sort_order");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));

  useEffect(() => {
    fetchProducts();
    axios.get(`${API}/products/ratings/summary`).then(r => setRatingSummary(r.data)).catch(() => {});
  }, [searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      params.set("per_page", "12");
      
      if (selectedSubjects.length > 0) params.set("subject", selectedSubjects.join(","));
      if (classLevel) params.set("class_level", classLevel);

      if (priceRange[0] > 0) params.set("min_price", priceRange[0].toString());
      if (priceRange[1] < 150) params.set("max_price", priceRange[1].toString());
      if (isBundle) params.set("is_bundle", "true");
      if (search) params.set("search", search);
      params.set("sort_by", sortBy);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setTotalProducts(response.data.total);
      setTotalPages(response.data.total_pages);
      // Extract unique class levels and subjects from products for dynamic filters
      const levels = [...new Set(response.data.products.map(p => p.class_level).filter(Boolean))];
      setDynamicClassLevels(prev => [...new Set([...prev, ...levels])].sort());
      const subjects = [...new Set(response.data.products.flatMap(p => Array.isArray(p.subject) ? p.subject : p.subject ? [p.subject] : []).filter(Boolean))];
      setDynamicSubjects(prev => [...new Set([...prev, ...subjects])].sort());
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (selectedSubjects.length > 0) params.set("subject", selectedSubjects.join(","));
    if (classLevel) params.set("class_level", classLevel);

    if (priceRange[0] > 0) params.set("min_price", priceRange[0].toString());
    if (priceRange[1] < 150) params.set("max_price", priceRange[1].toString());
    if (isBundle) params.set("is_bundle", "true");
    if (search) params.set("search", search);
    params.set("sort_by", sortBy);
    params.set("page", "1");
    
    setSearchParams(params);
    setPage(1);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setSelectedSubjects([]);
    setClassLevel("");
    setPriceRange([0, 150]);
    setIsBundle(false);
    setSearch("");
    setSortBy("sort_order");
    setSearchParams({});
    setPage(1);
  };

  const toggleSubject = (subject) => {
    setSelectedSubjects(prev =>
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const hasActiveFilters = selectedSubjects.length > 0 || classLevel || 
    priceRange[0] > 0 || priceRange[1] < 150 || isBundle || search;

  const filterContent = (
    <div className="space-y-6">
      {/* Search */}
      <div className="space-y-2">
        <Label className="font-medium">Search</Label>
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search exams..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="filter-search"
          />
        </form>
      </div>

      {/* Subjects */}
      <div className="space-y-3">
        <Label className="font-medium">Subjects</Label>
        <div className="space-y-2">
          {[...new Set([...SUBJECTS, ...dynamicSubjects])].map(subject => (
            <div key={subject} className="flex items-center gap-2">
              <Checkbox
                id={`subject-${subject}`}
                checked={selectedSubjects.includes(subject)}
                onCheckedChange={() => toggleSubject(subject)}
              />
              <label
                htmlFor={`subject-${subject}`}
                className="text-sm cursor-pointer"
              >
                {subject}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Class Level */}
      <div className="space-y-3">
        <Label className="font-medium">Class Level</Label>
        <RadioGroup value={classLevel} onValueChange={setClassLevel}>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="" id="level-all" />
            <label htmlFor="level-all" className="text-sm cursor-pointer">All Levels</label>
          </div>
          {[...new Set([...CLASS_LEVELS, ...dynamicClassLevels])].sort().map(level => (
            <div key={level} className="flex items-center gap-2">
              <RadioGroupItem value={level} id={`level-${level}`} />
              <label htmlFor={`level-${level}`} className="text-sm cursor-pointer">{level}</label>
            </div>
          ))}
        </RadioGroup>
      </div>


      {/* Price Range */}
      <div className="space-y-3">
        <Label className="font-medium">Price Range (GHS)</Label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={150}
          step={5}
          className="py-4"
        />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>GHS {priceRange[0]}</span>
          <span>GHS {priceRange[1]}</span>
        </div>
      </div>

      {/* Bundle Filter */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="is-bundle"
          checked={isBundle}
          onCheckedChange={setIsBundle}
        />
        <label htmlFor="is-bundle" className="text-sm cursor-pointer">
          Standard Question Bank
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4">
        <Button onClick={applyFilters} className="flex-1 bg-primary" data-testid="apply-filters">
          Apply Filters
        </Button>
        <Button onClick={clearFilters} variant="outline" data-testid="clear-filters">
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Banner with exam hall background */}
      <div className="relative bg-navy-900 overflow-hidden" data-testid="mock-exams-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('${classroomImg}')` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="text-gray-300 hover:text-white">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-500" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Mock Exams</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">Mock Examinations</h1>
          <p className="text-gray-300">
            {totalProducts} products available — GES-aligned BECE preparation materials
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div></div>

          <div className="flex items-center gap-3">
            {/* Mobile Filter Toggle */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-toggle">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {hasActiveFilters && (
                    <span className="ml-2 w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center">
                      {selectedSubjects.length + (classLevel ? 1 : 0)}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  {filterContent}
                </div>
              </SheetContent>
            </Sheet>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(value) => {
              setSortBy(value);
              setTimeout(applyFilters, 0);
            }}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sort_order">Relevance</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="title">Alphabetical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            {selectedSubjects.map(subject => (
              <Button
                key={subject}
                variant="secondary"
                size="sm"
                onClick={() => {
                  toggleSubject(subject);
                  setTimeout(applyFilters, 0);
                }}
              >
                {subject}
                <X className="w-3 h-3 ml-1" />
              </Button>
            ))}
            {classLevel && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setClassLevel("");
                  setTimeout(applyFilters, 0);
                }}
              >
                {classLevel}
                <X className="w-3 h-3 ml-1" />
              </Button>
            )}
            {search && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setTimeout(applyFilters, 0);
                }}
              >
                Search: "{search}"
                <X className="w-3 h-3 ml-1" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear all
            </Button>
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 bg-white p-6 rounded-xl border">
              <h2 className="font-heading font-bold text-lg mb-6">Filters</h2>
              {filterContent}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} rating={ratingSummary[product.id]} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => {
                        const newPage = page - 1;
                        setPage(newPage);
                        const params = new URLSearchParams(searchParams);
                        params.set("page", newPage.toString());
                        setSearchParams(params);
                      }}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => {
                        const newPage = page + 1;
                        setPage(newPage);
                        const params = new URLSearchParams(searchParams);
                        params.set("page", newPage.toString());
                        setSearchParams(params);
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <img src="/logo.png" alt="Great Messiah Publication" className="w-16 h-16 mx-auto mb-4 object-contain opacity-40 grayscale" />
                <h3 className="font-heading text-xl font-bold mb-2">No products found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or search query
                </p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
